import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  Bar, XAxis, YAxis, CartesianGrid, Legend, ComposedChart,
} from 'recharts';
import { useEnvironment } from '../../contexts/EnvironmentContext';
import { AMBRWorldMap } from './AMBRWorldMap';
import { FiscalYearPicker, timeAgo } from './FiscalYearPicker';
import { useProductAnalysis, useProductOrders, useAmbrAnalysis } from '../../hooks/queries';
import { useProductPreferences } from '../../hooks/useProductPreferences';
import {
  FiscalYear,
  getEffectivePeriod,
  filterRecordsByPeriod,
} from '../../utils/fiscal-year';
import {
  buildOrderLocationState,
  dashboardReturn,
} from '../../utils/order-navigation';
import { resolveInitialSku } from '../../utils/product-preferences';
import { trackUserEvent } from '../../utils/track-user-event';

/** FY filtering mixes period totals with lifetime breakdowns — keep off until fixed. */
const FISCAL_YEAR_PICKER_ENABLED = false;

/* ─── Client-side types (mirrors server warehouse.ts) ──────── */
interface ProductOrderRecord {
  orderId: number;
  date: string;
  country: string;
  countryIso2: string;
  standalone: boolean;
  coProducts: Array<{ name: string; sku: string }>;
  total?: number;
  paymentMethod?: string;
  status?: string;
}

interface SocietyOnlyOrderRecord {
  orderId: number;
  date: string;
  country: string;
  countryIso2: string;
  societySku: string;
  societyName: string;
  total?: number;
  paymentMethod?: string;
  status?: string;
}

interface AmbrSocietyData {
  withSociety: number;
  societyOnly: number;
  bySociety: Array<{ sku: string; name: string; withAmbr: number; standalone: number }>;
}

interface ProductAnalysis {
  sku: string;
  name: string;
  generatedAt: string;
  totalOrders: number;
  standalone: number;
  bundled: number;
  refunded: number;
  byDay: Array<{ date: string; total: number; standalone: number; bundled: number }>;
  companions: Array<{ name: string; sku: string; orderCount: number }>;
  byCountry: Array<{ country: string; countryIso2: string; total: number }>;
  byPaymentMethod: Array<{ method: string; orderCount: number }>;
  byStatus: Array<{ status: string; orderCount: number }>;
  societyData?: AmbrSocietyData;
  isLive?: boolean;
  bcAvailability?: string;
}

type DrillFilter =
  | { type: 'all' }
  | { type: 'standalone' }
  | { type: 'bundled' }
  | { type: 'refunded' }
  | { type: 'instituteOnly' }
  | { type: 'withSociety'; societySku?: string }
  | { type: 'societyOnlyDrill'; societySku?: string }
  | { type: 'country'; country: string }
  | { type: 'day'; date: string }
  | { type: 'companion'; productName: string }
  | { type: 'payment'; method: string }
  | { type: 'status'; status: string };

function drillDetail(filter: DrillFilter): string {
  switch (filter.type) {
    case 'country':
      return filter.country;
    case 'day':
      return filter.date;
    case 'companion':
      return filter.productName;
    case 'payment':
      return filter.method;
    case 'status':
      return filter.status;
    case 'withSociety':
    case 'societyOnlyDrill':
      return filter.societySku ?? filter.type;
    default:
      return filter.type;
  }
}

/* ─── Colours ────────────────────────────────────────────────── */
const PAYMENT_COLORS = ['#2563eb', '#7c3aed', '#0891b2', '#0d9488', '#d97706', '#94a3b8'];

function statusColor(status: string): string {
  const s = status.toLowerCase();
  if (s.includes('complet') || s.includes('fulfil') || s.includes('shipped')) { return '#16a34a'; }
  if (s.includes('await') || s.includes('pending') || s.includes('processing')) { return '#d97706'; }
  if (s.includes('cancel') || s.includes('declin') || s.includes('void')) { return '#dc2626'; }
  if (s.includes('refund')) { return '#f59e0b'; }
  return '#94a3b8';
}

/* ─── Chart tooltip ──────────────────────────────────────────── */
const ChartTip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) { return null; }
  return (
    <div style={{ background: '#0f172a', color: '#f8fafc', padding: '0.6rem 0.9rem', borderRadius: 8, fontSize: '0.8rem', boxShadow: '0 8px 24px rgba(0,0,0,0.3)' }}>
      {label && <div style={{ marginBottom: '0.3rem', opacity: 0.6, fontWeight: 600 }}>{label}</div>}
      {payload.map((p: any) => (
        <div key={p.name} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: 2 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: p.color || p.fill, flexShrink: 0 }} />
          <span style={{ opacity: 0.8 }}>{p.name}:</span>
          <span style={{ fontWeight: 700 }}>{p.value}</span>
        </div>
      ))}
    </div>
  );
};

/* ─── Payment/Status donut ───────────────────────────────────── */
function PaymentStatusDonut({ data, total, onSliceClick }: {
  data: Array<{ label: string; value: number; color: string }>;
  total: number;
  onSliceClick: (label: string) => void;
}) {
  const safeTotal = Math.max(total, 1);
  return (
    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
      <div style={{ flexShrink: 0 }}>
        <ResponsiveContainer width={110} height={110}>
          <PieChart>
            <Pie data={data.map(d => ({ ...d, name: d.label }))} cx="50%" cy="50%"
              innerRadius={30} outerRadius={50} paddingAngle={2} dataKey="value" strokeWidth={0}
              onClick={(d: any) => d?.name && onSliceClick(d.name)}>
              {data.map((d, i) => <Cell key={i} fill={d.color} style={{ cursor: 'pointer' }} />)}
            </Pie>
            <text x="50%" y="50%" textAnchor="middle" dominantBaseline="central">
              <tspan x="50%" dy="-0.25em" fontSize="1.1rem" fontWeight="800" fill="#0f172a">{total}</tspan>
              <tspan x="50%" dy="1.2em" fontSize="0.58rem" fill="#94a3b8" fontWeight="600" letterSpacing="0.06em">ORDERS</tspan>
            </text>
            <Tooltip content={<ChartTip />} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
        {data.map((d, i) => (
          <div key={i} onClick={() => onSliceClick(d.label)}
            style={{ cursor: 'pointer', borderRadius: 6, padding: '0.15rem 0.3rem', margin: '0 -0.3rem', transition: 'background 0.15s' }}
            onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.background = '#f8fafc'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.15rem', fontSize: '0.78rem' }}>
              <span style={{ color: '#374151', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '0.3rem', maxWidth: '70%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: d.color, flexShrink: 0 }} />
                {d.label}
              </span>
              <span style={{ fontWeight: 700, color: d.color, flexShrink: 0 }}>
                {d.value} <span style={{ color: '#94a3b8', fontWeight: 400 }}>({Math.round(d.value / safeTotal * 100)}%)</span>
                <span style={{ color: '#cbd5e1', marginLeft: '0.3rem', fontSize: '0.7rem' }}>→</span>
              </span>
            </div>
            <div style={{ height: 4, background: '#f1f5f9', borderRadius: 999, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${(d.value / safeTotal) * 100}%`, background: d.color, borderRadius: 999 }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Drill-down drawer ──────────────────────────────────────── */
function DrillDrawer({ orders, societyOnlyOrders = [], filter, productSku, onClose, isLoading }: {
  orders: ProductOrderRecord[];
  societyOnlyOrders?: SocietyOnlyOrderRecord[];
  filter: DrillFilter;
  productSku: string;
  onClose: () => void;
  isLoading?: boolean;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const dashboardBack = dashboardReturn(location.pathname);

  if (filter.type === 'societyOnlyDrill') {
    const filteredSoc = filter.societySku
      ? societyOnlyOrders.filter(o => o.societySku === filter.societySku)
      : societyOnlyOrders;
    const code = filter.societySku ? filter.societySku.replace(/^ASD-/i, '') : undefined;
    const title = code ? `Society Only — ${code}` : 'All Society-Only Orders';
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 400, display: 'flex', justifyContent: 'flex-end' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)' }} onClick={onClose} />
        <div className="ambr-drawer-panel" style={{ position: 'relative', width: 520, maxWidth: '95vw', height: '100%', background: '#fff', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fff', flexShrink: 0 }}>
            <div style={{ paddingRight: '2.5rem' }}>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#0f172a' }}>{title}</h3>
              <p style={{ margin: '0.2rem 0 0', fontSize: '0.8rem', color: '#94a3b8' }}>{filteredSoc.length} order{filteredSoc.length !== 1 ? 's' : ''} · tap row to view details</p>
            </div>
            <button onClick={onClose} style={{ position: 'absolute', top: '0.875rem', right: '1rem', width: 36, height: 36, borderRadius: '50%', background: '#f1f5f9', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem', color: '#475569' }}>✕</button>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', paddingBottom: '60px' }}>
            {filteredSoc.length === 0 ? (
              <div style={{ padding: '3rem', textAlign: 'center', color: '#94a3b8' }}>No orders found</div>
            ) : filteredSoc.map(o => (
              <div key={`${o.orderId}-${o.societySku}`}
                onClick={() => navigate(`/orders/${o.orderId}`, { state: buildOrderLocationState(dashboardBack) })}
                style={{ padding: '0.875rem 1.5rem', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', transition: 'background 0.1s' }}
                onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.background = '#f8fafc'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = '#fff'; }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.35rem' }}>
                  <span style={{ fontWeight: 700, color: '#0891b2', fontSize: '0.9rem' }}>#{o.orderId}</span>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.72rem', fontWeight: 600, padding: '0.15rem 0.5rem', borderRadius: 999, background: '#ecfeff', color: '#0891b2', border: '1px solid #a5f3fc' }}>
                      {o.societySku.replace(/^ASD-/i, '')} Society
                    </span>
                    {o.total !== undefined && (
                      <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#0f172a' }}>
                        ${o.total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '1rem', fontSize: '0.8rem', color: '#64748b', flexWrap: 'wrap' }}>
                  <span>{o.date}</span>
                  <span>{o.country}</span>
                  {o.status && <span style={{ color: statusColor(o.status), fontWeight: 500 }}>{o.status}</span>}
                  {o.paymentMethod && <span style={{ color: '#94a3b8' }}>{o.paymentMethod}</span>}
                </div>
              </div>
            ))}
          </div>
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '0.75rem 1.5rem', background: '#fff', borderTop: '1px solid #e2e8f0', boxShadow: '0 -4px 12px rgba(0,0,0,0.08)' }}>
            <button onClick={onClose} style={{ width: '100%', padding: '0.75rem', background: '#1a2332', color: '#fff', border: 'none', borderRadius: 10, fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer' }}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  const filtered = isLoading ? [] : orders.filter(o => {
    if (filter.type === 'standalone')    { return o.standalone; }
    if (filter.type === 'bundled')       { return !o.standalone; }
    if (filter.type === 'refunded')      { return (o.status ?? '').toLowerCase().includes('refund'); }
    if (filter.type === 'instituteOnly') { return !o.coProducts.some(cp => cp.sku.toUpperCase().startsWith('ASD-')); }
    if (filter.type === 'withSociety')   {
      const hasSociety = o.coProducts.some(cp => cp.sku.toUpperCase().startsWith('ASD-'));
      if (!hasSociety) { return false; }
      return filter.societySku ? o.coProducts.some(cp => cp.sku.toUpperCase() === filter.societySku!.toUpperCase()) : true;
    }
    if (filter.type === 'country')    { return o.country === filter.country; }
    if (filter.type === 'day')        { return o.date === filter.date; }
    if (filter.type === 'companion')  { return !o.standalone && o.coProducts.some(cp => cp.name === filter.productName); }
    if (filter.type === 'payment')    { return (o.paymentMethod ?? '') === filter.method; }
    if (filter.type === 'status')     { return (o.status ?? '') === filter.status; }
    return true;
  });

  const title =
    filter.type === 'standalone'    ? `Standalone ${productSku} Orders` :
    filter.type === 'bundled'       ? `Bundled ${productSku} Orders` :
    filter.type === 'refunded'      ? `Refunded ${productSku} Orders` :
    filter.type === 'instituteOnly' ? 'CFA Institute Associate Only' :
    filter.type === 'withSociety' && filter.societySku ? `${productSku} + Society (${filter.societySku.replace(/^ASD-/i, '')})` :
    filter.type === 'withSociety'   ? `Bought Both: ${productSku} + Society` :
    filter.type === 'country'       ? `${productSku} Orders — ${filter.country}` :
    filter.type === 'day'           ? `${productSku} Orders — ${filter.date}` :
    filter.type === 'companion'     ? `Bundled with: ${filter.productName}` :
    filter.type === 'payment'       ? `Paid by: ${filter.method}` :
    filter.type === 'status'        ? `Status: ${filter.status}` :
    `All ${productSku} Orders`;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 400, display: 'flex', justifyContent: 'flex-end' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)' }} onClick={onClose} />
      <div className="ambr-drawer-panel" style={{ position: 'relative', width: 520, maxWidth: '95vw', height: '100%', background: '#fff', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fff', flexShrink: 0 }}>
          <div style={{ paddingRight: '2.5rem' }}>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#0f172a' }}>{title}</h3>
            <p style={{ margin: '0.2rem 0 0', fontSize: '0.8rem', color: '#94a3b8' }}>
              {isLoading ? 'Loading orders…' : `${filtered.length} order${filtered.length !== 1 ? 's' : ''} · tap row to view details`}
            </p>
          </div>
          <button onClick={onClose} style={{ position: 'absolute', top: '0.875rem', right: '1rem', width: 36, height: 36, borderRadius: '50%', background: '#f1f5f9', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem', color: '#475569' }}>✕</button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', paddingBottom: '60px' }}>
          {isLoading ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: '#94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem' }}>
              <div style={{ width: 18, height: 18, borderRadius: '50%', border: '2px solid #e2e8f0', borderTopColor: '#2563eb', animation: 'spin 0.7s linear infinite' }} />
              Loading orders…
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: '#94a3b8' }}>No orders found</div>
          ) : filtered.map(o => (
            <div
              key={o.orderId}
              onClick={() => navigate(`/orders/${o.orderId}`, { state: buildOrderLocationState(dashboardBack) })}
              style={{ padding: '0.875rem 1.5rem', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', transition: 'background 0.1s' }}
              onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.background = '#f8fafc'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = '#fff'; }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.35rem' }}>
                <span style={{ fontWeight: 700, color: '#2563eb', fontSize: '0.9rem' }}>#{o.orderId}</span>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.72rem', fontWeight: 600, padding: '0.15rem 0.5rem', borderRadius: 999, background: o.standalone ? '#f0fdf4' : '#eff6ff', color: o.standalone ? '#16a34a' : '#2563eb', border: `1px solid ${o.standalone ? '#bbf7d0' : '#bfdbfe'}` }}>
                    {o.standalone ? 'Standalone' : 'Bundled'}
                  </span>
                  {o.total !== undefined && (
                    <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#0f172a' }}>
                      ${o.total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '1rem', fontSize: '0.8rem', color: '#64748b', flexWrap: 'wrap' }}>
                <span>{o.date}</span>
                <span>{o.country}</span>
                {o.status && <span style={{ color: statusColor(o.status), fontWeight: 500 }}>{o.status}</span>}
                {o.paymentMethod && <span style={{ color: '#94a3b8' }}>{o.paymentMethod}</span>}
              </div>
              {o.coProducts.length > 0 && (
                <div style={{ marginTop: '0.4rem', fontSize: '0.75rem', color: '#94a3b8' }}>
                  + {o.coProducts.slice(0, 3).map(cp => cp.name).join(', ')}
                  {o.coProducts.length > 3 && ` +${o.coProducts.length - 3} more`}
                </div>
              )}
            </div>
          ))}
        </div>
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '0.75rem 1.5rem', background: '#fff', borderTop: '1px solid #e2e8f0', boxShadow: '0 -4px 12px rgba(0,0,0,0.08)' }}>
          <button onClick={onClose} style={{ width: '100%', padding: '0.75rem', background: '#1a2332', color: '#fff', border: 'none', borderRadius: 10, fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer' }}>Close</button>
        </div>
      </div>
    </div>
  );
}

function getTrend(p: ProductAnalysis): '↑' | '↓' | '→' | null {
  const days = p.byDay;
  if (days.length < 8) { return null; }
  const recent = days.slice(-7).reduce((s, d) => s + d.total, 0);
  const prior  = days.slice(-14, -7).reduce((s, d) => s + d.total, 0);
  if (prior === 0) { return recent > 0 ? '↑' : null; }
  const pct = (recent - prior) / prior;
  if (pct > 0.05) { return '↑'; }
  if (pct < -0.05) { return '↓'; }
  return '→';
}

/* ─── Main Component ─────────────────────────────────────────── */
export function ProductAnalyticsDashboard({ defaultSku }: { defaultSku?: string }) {
  const { environment } = useEnvironment();
  const [searchParams] = useSearchParams();
  const {
    lastSku,
    pins,
    setLastSku,
    pin,
    unpin,
    pruneToCatalog,
  } = useProductPreferences();
  const [fiscalYear, setFiscalYear] = useState<FiscalYear>('FY26');
  const [selectedSku, setSelectedSku] = useState<string>(defaultSku ?? 'AMBR');
  const [drill, setDrill] = useState<DrillFilter | null>(null);
  const [geoView, setGeoView] = useState<'list' | 'map'>('map');
  const [showAllCountries, setShowAllCountries] = useState(false);
  const [societyView, setSocietyView] = useState<'combined' | 'standalone'>('combined');
  const [activeTab, setActiveTab] = useState<'overview' | 'society'>('overview');
  const [pinLimitMessage, setPinLimitMessage] = useState<string | null>(null);

  // Product selector dropdown state
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [filterText, setFilterText] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const hasInitializedSku = useRef(false);
  const pageViewTracked = useRef(false);

  const apiFiscalYear = FISCAL_YEAR_PICKER_ENABLED ? fiscalYear : undefined;

  const { data: paResp, isLoading: paLoading } = useProductAnalysis(apiFiscalYear);
  const productAnalysisFile = paResp?.success ? paResp.data : null;
  const effectivePeriod = FISCAL_YEAR_PICKER_ENABLED
    ? getEffectivePeriod(fiscalYear)
    : {
        start: productAnalysisFile?.periodStart ?? '',
        end: productAnalysisFile?.periodEnd ?? new Date().toISOString().slice(0, 10),
      };

  // Keep full AMBR data for societyOnlyOrders in the AMBR drilldown drawer
  const { data: ambrResp } = useAmbrAnalysis();
  const ambrData = ambrResp?.success ? ambrResp.data : null;

  // Lazy-load per-product order records only when a drilldown is opened
  const { data: ordersResp, isLoading: ordersLoading } = useProductOrders(
    selectedSku,
    drill !== null && drill.type !== 'societyOnlyDrill',
    apiFiscalYear,
  );
  const productOrders = ordersResp?.success ? ordersResp.data : null;

  const filteredSocietyOnlyOrders = FISCAL_YEAR_PICKER_ENABLED
    ? filterRecordsByPeriod(ambrData?.societyOnlyOrders ?? [], effectivePeriod)
    : (ambrData?.societyOnlyOrders ?? []);

  // Build sorted product list: AMBR first, then alphabetical; hide ASD- society products
  const products: Record<string, ProductAnalysis> = productAnalysisFile?.products ?? {};
  const productList: ProductAnalysis[] = Object.values(products)
    .filter(p => !p.sku.toUpperCase().startsWith('ASD-'))
    .sort((a, b) => {
      if (a.sku === 'AMBR') { return -1; }
      if (b.sku === 'AMBR') { return 1; }
      return a.name.localeCompare(b.name);
    });

  // Sync selectedSku once when productAnalysisFile first loads
  useEffect(() => {
    if (!productAnalysisFile || hasInitializedSku.current) { return; }
    hasInitializedSku.current = true;

    const catalogSkus = productList.map((p) => p.sku);
    const catalogUpper = new Set(catalogSkus.map((s) => s.toUpperCase()));
    let effectiveLast = lastSku;
    if (effectiveLast && !catalogUpper.has(effectiveLast.toUpperCase())) {
      effectiveLast = catalogUpper.has('AMBR') ? 'AMBR' : null;
    }
    pruneToCatalog(catalogSkus);

    const querySku = searchParams.get('sku');
    const resolved = resolveInitialSku({
      catalogSkus,
      forcedSku: defaultSku,
      querySku,
      lastSku: effectiveLast,
    });
    setSelectedSku(resolved);

    // Update lastSku for query/pin navigation; never overwrite solely due to /ambr mount
    const queryNormalized = querySku?.trim().toUpperCase() ?? '';
    if (!defaultSku && queryNormalized && products[queryNormalized]) {
      setLastSku(queryNormalized);
    }
  }, [productAnalysisFile]); // eslint-disable-line react-hooks/exhaustive-deps

  // When ?sku= changes after mount (sidebar pin click while already on /analytics)
  useEffect(() => {
    if (!productAnalysisFile || !hasInitializedSku.current) {
      return;
    }
    const querySku = searchParams.get('sku');
    if (!querySku || defaultSku) {
      return;
    }
    const normalized = querySku.trim().toUpperCase();
    if (!products[normalized] || normalized === selectedSku) {
      return;
    }
    setSelectedSku(normalized);
    setLastSku(normalized);
    setDrill(null);
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!productAnalysisFile || pageViewTracked.current) {
      return;
    }
    pageViewTracked.current = true;
    trackUserEvent('product_analytics.page_viewed', {
      sku: selectedSku,
      fiscalYear,
      ...(defaultSku ? { defaultSku } : {}),
    });
  }, [productAnalysisFile, selectedSku, fiscalYear, defaultSku]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!dropdownOpen) { return; }
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
        setFilterText('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [dropdownOpen]);

  // Auto-focus search input when dropdown opens
  useEffect(() => {
    if (dropdownOpen) {
      setTimeout(() => searchInputRef.current?.focus(), 30);
    }
  }, [dropdownOpen]);

  // Close drilldown and reset on product change
  const handleSkuChange = (sku: string) => {
    if (sku === selectedSku) {
      return;
    }
    setSelectedSku(sku);
    setLastSku(sku);
    setPinLimitMessage(null);
    setDrill(null);
    setShowAllCountries(false);
    const productName = products[sku]?.name;
    trackUserEvent('product_analytics.sku_selected', {
      sku,
      ...(productName ? { productName } : {}),
    });
  };

  const isCurrentPinned = pins.some((p) => p.sku === selectedSku.toUpperCase());

  const handlePinToggle = () => {
    if (isCurrentPinned) {
      unpin(selectedSku);
      setPinLimitMessage(null);
      return;
    }
    const result = pin({
      sku: selectedSku,
      name: products[selectedSku]?.name ?? selectedSku,
    });
    if (!result.ok && result.reason === 'limit') {
      setPinLimitMessage('You can pin up to 5 products. Unpin one to add another.');
      return;
    }
    setPinLimitMessage(null);
  };

  const handleFiscalYearChange = (fy: FiscalYear) => {
    setFiscalYear(fy);
    setDrill(null);
    trackUserEvent('product_analytics.fiscal_year_changed', { fiscalYear: fy });
  };

  const openDrill = (filter: DrillFilter) => {
    setDrill(filter);
    trackUserEvent('product_analytics.drilldown_opened', {
      drillType: filter.type,
      sku: selectedSku,
      detail: drillDetail(filter),
    });
  };

  // Loading / error states
  const paError = paResp?.success === false
    ? (paResp.message ?? 'Failed to load product analysis')
    : null;

  if (paLoading) {
    return (
      <div style={{ padding: '2rem', display: 'flex', alignItems: 'center', gap: '0.75rem', color: '#94a3b8' }}>
        <div style={{ width: 20, height: 20, borderRadius: '50%', border: '2px solid #e2e8f0', borderTopColor: '#2563eb', animation: 'spin 0.7s linear infinite' }} />
        Loading product analytics…
      </div>
    );
  }

  if (paError || !productAnalysisFile) {
    return (
      <div style={{ padding: '2rem' }}>
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '1rem', color: '#dc2626', fontSize: '0.875rem' }}>
          ⚠ {paError ?? 'Product analysis not yet initialised. Run csv-to-warehouse.js or the refresh Lambda first.'}
        </div>
      </div>
    );
  }

  const data: ProductAnalysis | undefined = products[selectedSku];

  if (!data) {
    return (
      <div style={{ padding: '2rem' }}>
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '1rem', color: '#dc2626', fontSize: '0.875rem' }}>
          ⚠ No data found for SKU: {selectedSku}
        </div>
      </div>
    );
  }

  const isAmbrSelected = selectedSku === 'AMBR';
  const societyData = isAmbrSelected ? data.societyData : undefined;
  const donutData = [
    { name: 'Bundled',    value: data.bundled,    color: '#2563eb' },
    { name: 'Standalone', value: data.standalone, color: '#16a34a' },
  ];
  const maxCompanion = Math.max(...data.companions.map(c => c.orderCount), 1);
  const maxCountry   = Math.max(...data.byCountry.map(c => c.total), 1);
  const refundedCount = data.refunded ?? 0;
  const netOrders = data.totalOrders - refundedCount;
  const dataRangeStart = productAnalysisFile.periodStart ?? effectivePeriod.start;
  const dataRangeEnd = productAnalysisFile.periodEnd ?? effectivePeriod.end;
  // Prefer lastSyncedAt (advanced every refresh, even with no new orders) over
  // generatedAt (only rewritten when SKU analysis changes).
  const refreshedAt =
    (productAnalysisFile as { lastSyncedAt?: string }).lastSyncedAt
    ?? productAnalysisFile.generatedAt;
  const periodLabel = FISCAL_YEAR_PICKER_ENABLED
    ? fiscalYear
    : (dataRangeStart && dataRangeEnd
      ? `${dataRangeStart} – ${dataRangeEnd}`
      : 'All warehouse data');
  const kpi = [
    { label: 'Units Sold', value: data.totalOrders, testId: 'kpi-total',      sub: periodLabel, color: '#2563eb', bg: '#eff6ff',  drill: { type: 'all' } as DrillFilter },
    { label: 'Refunded',                value: refundedCount,    testId: 'kpi-refunded',   sub: `${netOrders} net (of ${data.totalOrders})`, color: '#dc2626', bg: '#fef2f2', drill: { type: 'refunded' } as DrillFilter },
    { label: 'Standalone',              value: data.standalone,  testId: 'kpi-standalone', sub: `${(100*data.standalone/Math.max(data.totalOrders,1)).toFixed(1)}% of orders`, color: '#16a34a', bg: '#f0fdf4', drill: { type: 'standalone' } as DrillFilter },
    { label: 'Bundled',                 value: data.bundled,     testId: 'kpi-bundled',    sub: `${(100*data.bundled/Math.max(data.totalOrders,1)).toFixed(1)}% of orders`,    color: '#f59e0b', bg: '#fffbeb', drill: { type: 'bundled' } as DrillFilter },
    { label: 'Countries Reached',       value: data.byCountry.length, testId: 'kpi-countries', sub: periodLabel, color: '#7c3aed', bg: '#f5f3ff', drill: null },
  ];

  return (
    <div className="ambr-container" style={{ fontFamily: "'DM Sans','Segoe UI',system-ui,sans-serif", background: '#f8fafc', minHeight: '100vh' }}>

      {/* Header */}
      <div className="ambr-header">
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
            <h1 style={{ margin: 0, fontSize: '1.375rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.025em' }}>
              {data.name}
            </h1>
            <span style={{ background: 'linear-gradient(135deg,#16a34a,#15803d)', color: '#fff', fontSize: '0.7rem', fontWeight: 700, padding: '0.2rem 0.6rem', borderRadius: 999, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              LIVE · {environment.toUpperCase()}
            </span>
          </div>

          {/* Rich product selector + pin */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <div ref={dropdownRef} style={{ position: 'relative', display: 'inline-block' }}>
            {/* Trigger button */}
            <button
              onClick={() => { setDropdownOpen(o => !o); setFilterText(''); }}
              onKeyDown={e => { if (e.key === 'Escape') { setDropdownOpen(false); setFilterText(''); } }}
              aria-haspopup="listbox"
              aria-expanded={dropdownOpen}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.6rem',
                background: '#fff', border: `1.5px solid ${dropdownOpen ? '#2563eb' : '#e2e8f0'}`,
                borderRadius: 10, padding: '0.5rem 0.9rem',
                cursor: 'pointer', outline: 'none',
                boxShadow: dropdownOpen ? '0 0 0 3px rgba(37,99,235,0.12)' : '0 1px 3px rgba(0,0,0,0.06)',
                transition: 'border-color 0.15s, box-shadow 0.15s', minWidth: 240,
              }}
            >
              <span style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: data.isLive === false ? '#94a3b8' : '#16a34a', display: 'inline-block' }} />
              <span style={{ flex: 1, textAlign: 'left' }}>
                <span style={{ fontSize: '0.875rem', fontWeight: 700, color: '#0f172a' }}>{data.name}</span>
                <span style={{ fontSize: '0.72rem', color: '#94a3b8', marginLeft: '0.4rem', fontWeight: 500 }}>{selectedSku}</span>
              </span>
              {data.isLive === false && (
                <span style={{ fontSize: '0.65rem', fontWeight: 700, color: '#b45309', background: '#fef3c7', borderRadius: 999, padding: '0.1rem 0.4rem', letterSpacing: '0.04em' }}>INACTIVE</span>
              )}
              <span style={{ fontSize: '0.65rem', color: '#94a3b8', marginLeft: '0.2rem' }}>{dropdownOpen ? '▲' : '▼'}</span>
            </button>

            {/* Dropdown panel */}
            {dropdownOpen && (
              <div
                role="listbox"
                style={{
                  position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 200,
                  background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12,
                  boxShadow: '0 8px 30px rgba(0,0,0,0.12)', minWidth: 320, overflow: 'hidden',
                }}
              >
                {/* Search */}
                <div style={{ padding: '0.6rem 0.75rem', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>⌕</span>
                  <input
                    ref={searchInputRef}
                    value={filterText}
                    onChange={e => setFilterText(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Escape') { setDropdownOpen(false); setFilterText(''); } }}
                    placeholder="Filter products…"
                    style={{ border: 'none', outline: 'none', fontSize: '0.85rem', color: '#0f172a', background: 'transparent', width: '100%' }}
                  />
                  {filterText && (
                    <button onClick={() => setFilterText('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '0.8rem', padding: 0 }}>✕</button>
                  )}
                </div>

                {/* Product rows */}
                <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                  {productList
                    .filter(p => {
                      const q = filterText.toLowerCase();
                      return !q || p.name.toLowerCase().includes(q) || p.sku.toLowerCase().includes(q);
                    })
                    .map(p => {
                      const isSelected = p.sku === selectedSku;
                      const isInactive = p.isLive === false;
                      const trend = getTrend(p);
                      return (
                        <button
                          key={p.sku}
                          role="option"
                          aria-selected={isSelected}
                          onClick={() => { handleSkuChange(p.sku); setDropdownOpen(false); setFilterText(''); }}
                          style={{
                            width: '100%', display: 'flex', alignItems: 'center', gap: '0.65rem',
                            padding: '0.6rem 0.85rem', background: isSelected ? '#eff6ff' : 'transparent',
                            border: 'none', borderLeft: `3px solid ${isSelected ? '#2563eb' : 'transparent'}`,
                            cursor: 'pointer', textAlign: 'left', opacity: isInactive ? 0.65 : 1,
                            transition: 'background 0.1s',
                          }}
                          onMouseEnter={e => { if (!isSelected) { (e.currentTarget as HTMLButtonElement).style.background = '#f8fafc'; } }}
                          onMouseLeave={e => { if (!isSelected) { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; } }}
                        >
                          <span style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: isInactive ? '#94a3b8' : '#16a34a' }} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '0.85rem', fontWeight: isSelected ? 700 : 500, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {p.name}
                            </div>
                            <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: 1 }}>{p.sku}</div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexShrink: 0 }}>
                            <span style={{ fontSize: '0.78rem', fontWeight: 600, color: isSelected ? '#2563eb' : '#475569' }}>
                              {p.totalOrders.toLocaleString()}
                            </span>
                            {trend && (
                              <span style={{ fontSize: '0.75rem', fontWeight: 700, color: trend === '↑' ? '#16a34a' : trend === '↓' ? '#dc2626' : '#94a3b8' }}>{trend}</span>
                            )}
                            {isInactive && (
                              <span style={{ fontSize: '0.62rem', fontWeight: 700, color: '#b45309', background: '#fef3c7', borderRadius: 999, padding: '0.1rem 0.35rem' }}>inactive</span>
                            )}
                          </div>
                        </button>
                      );
                    })
                  }
                </div>

                {/* Footer hint */}
                <div style={{ padding: '0.4rem 0.85rem', borderTop: '1px solid #f1f5f9', fontSize: '0.68rem', color: '#cbd5e1' }}>
                  {productList.length} products · click to select · Esc to close
                </div>
              </div>
            )}
          </div>
            <button
              type="button"
              onClick={handlePinToggle}
              aria-label={isCurrentPinned ? 'Unpin product' : 'Pin product'}
              data-testid="product-pin-toggle"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.35rem',
                background: isCurrentPinned ? '#eff6ff' : '#fff',
                border: `1.5px solid ${isCurrentPinned ? '#2563eb' : '#e2e8f0'}`,
                borderRadius: 10,
                padding: '0.5rem 0.85rem',
                cursor: 'pointer',
                fontSize: '0.8rem',
                fontWeight: 600,
                color: isCurrentPinned ? '#2563eb' : '#475569',
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
              }}
            >
              <span aria-hidden="true">{isCurrentPinned ? '📌' : '📍'}</span>
              {isCurrentPinned ? 'Unpin' : 'Pin'}
            </button>
            {pinLimitMessage && (
              <span
                role="status"
                data-testid="pin-limit-message"
                style={{ fontSize: '0.75rem', color: '#b45309', fontWeight: 500 }}
              >
                {pinLimitMessage}
              </span>
            )}
          </div>
        </div>

        {FISCAL_YEAR_PICKER_ENABLED ? (
          <FiscalYearPicker
            value={fiscalYear}
            onChange={handleFiscalYearChange}
            refreshedAt={refreshedAt}
          />
        ) : (
          <div
            data-testid="data-range-note"
            style={{
              background: '#fff',
              border: '1px solid #e2e8f0',
              borderRadius: 10,
              padding: '0.6rem 1rem',
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            }}
          >
            <div
              style={{
                fontSize: '0.7rem',
                color: '#94a3b8',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
              }}
            >
              Data range
            </div>
            <div style={{ marginTop: '0.25rem', fontSize: '0.9rem', fontWeight: 700, color: '#0f172a' }}>
              {dataRangeStart && dataRangeEnd
                ? `${dataRangeStart} – ${dataRangeEnd}`
                : 'All warehouse data'}
            </div>
            {refreshedAt && (
              <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: '0.1rem' }}>
                Refreshed {timeAgo(refreshedAt)}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Disclaimer banner */}
      <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '0.65rem 1rem', marginBottom: '1.25rem', display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
        <span style={{ fontSize: '0.95rem', lineHeight: 1 }}>⚠️</span>
        <p style={{ margin: 0, fontSize: '0.8rem', color: '#92400e', lineHeight: 1.5 }}>
          <strong>Disclaimer:</strong> This dashboard is an engineering tool for operational support only and should not be used as an authoritative data source. Numbers are directionally correct but may not reflect 100% accuracy. For official membership numbers, please contact <strong>Will Morrison</strong> at{' '}
          <a href="mailto:will.morrison@cfainstitute.org" style={{ color: '#92400e' }}>will.morrison@cfainstitute.org</a>.
        </p>
      </div>

      {/* Tab bar — Society tab only for AMBR */}
      <div style={{ display: 'flex', borderBottom: '2px solid #e2e8f0', marginBottom: '1.25rem' }}>
        {([
          { id: 'overview' as const, label: 'Overview' },
          ...(isAmbrSelected ? [{ id: 'society' as const, label: 'Society Overlap' }] : []),
        ]).map(tab => (
          <button key={tab.id} onClick={() => {
            setActiveTab(tab.id);
            trackUserEvent('product_analytics.tab_changed', { tab: tab.id });
          }} style={{
            padding: '0.65rem 1.5rem', background: 'none', border: 'none',
            borderBottom: `2px solid ${activeTab === tab.id ? '#2563eb' : 'transparent'}`,
            marginBottom: -2, fontSize: '0.875rem', fontWeight: activeTab === tab.id ? 700 : 500,
            color: activeTab === tab.id ? '#2563eb' : '#64748b', cursor: 'pointer',
            transition: 'color 0.15s',
          }}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Overview tab ─────────────────────────────────────── */}
      {activeTab === 'overview' && <>

        {/* KPI row */}
        <div className="ambr-kpi-grid">
          {kpi.map((k, i) => (
            <div
              key={i}
              onClick={() => k.drill && openDrill(k.drill)}
              style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', position: 'relative', overflow: 'hidden', cursor: k.drill ? 'pointer' : 'default', transition: 'box-shadow 0.15s, transform 0.15s' }}
              onMouseEnter={e => { if (k.drill) { (e.currentTarget as HTMLDivElement).style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'; (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-1px)'; } }}
              onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)'; (e.currentTarget as HTMLDivElement).style.transform = 'none'; }}
            >
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: k.color }} />
              <div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.5rem' }}>{k.label}</div>
              <div className="ambr-kpi-value" data-testid={k.testId} style={{ fontSize: '2.25rem', fontWeight: 800, color: k.color, lineHeight: 1, marginBottom: '0.35rem', letterSpacing: '-0.03em' }}>{k.value}</div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', background: k.bg, color: k.color, fontSize: '0.72rem', fontWeight: 600, padding: '0.15rem 0.5rem', borderRadius: 999 }}>
                {k.sub}{k.drill && <span style={{ opacity: 0.6 }}>→</span>}
              </div>
            </div>
          ))}
        </div>

        {/* Row 2: Geographic Reach | Top Bundle Companions */}
        <div className="ambr-mid-grid" style={{ marginBottom: '1.25rem' }}>

          {/* Geographic Reach */}
          <div className="ambr-card" style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
              <h3 style={{ margin: 0, fontSize: '0.875rem', fontWeight: 700, color: '#0f172a' }}>Geographic Reach</h3>
              <div style={{ display: 'flex', gap: 2, background: '#f1f5f9', borderRadius: 8, padding: 2 }}>
                {(['list', 'map'] as const).map(v => (
                  <button key={v} onClick={() => setGeoView(v)} style={{ padding: '0.25rem 0.6rem', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600, background: geoView === v ? '#fff' : 'transparent', color: geoView === v ? '#2563eb' : '#94a3b8', boxShadow: geoView === v ? '0 1px 3px rgba(0,0,0,0.1)' : 'none', transition: 'all 0.15s' }}>
                    {v === 'list' ? '≡ List' : '🗺 Map'}
                  </button>
                ))}
              </div>
            </div>
            <p style={{ margin: '0 0 0.75rem', fontSize: '0.75rem', color: '#94a3b8' }}>
              {geoView === 'map' ? 'Hover a country for details · click to drill in' : 'Click a country to drill into its orders'}
            </p>
            {geoView === 'map' && (
              <AMBRWorldMap
                byCountry={data.byCountry.map(c => ({ ...c, standalone: 0, bundled: c.total }))}
                onCountryClick={(country) => openDrill({ type: 'country', country })}
              />
            )}
            {geoView === 'list' && (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
                  {(showAllCountries ? data.byCountry : data.byCountry.slice(0, 10)).map((c, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', cursor: 'pointer' }}
                      onClick={() => openDrill({ type: 'country', country: c.country })}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.2rem' }}>
                          <span style={{ fontSize: '0.82rem', color: '#334155', fontWeight: 500 }}>{c.country}</span>
                          <span style={{ fontSize: '0.82rem', color: '#0f172a', fontWeight: 700 }}>{c.total}</span>
                        </div>
                        <div style={{ height: 5, background: '#f1f5f9', borderRadius: 999, overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${(c.total / maxCountry) * 100}%`, background: '#2563eb', borderRadius: 999 }} />
                        </div>
                      </div>
                    </div>
                  ))}
                  {data.byCountry.length > 10 && (
                    <button onClick={() => setShowAllCountries(v => !v)}
                      style={{ width: '100%', background: 'none', border: 'none', fontSize: '0.75rem', color: '#2563eb', cursor: 'pointer', padding: '0.35rem 0', textAlign: 'center', fontWeight: 500 }}>
                      {showAllCountries ? '▲ Show less' : `▼ +${data.byCountry.length - 10} more countries`}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Top Bundle Companions */}
          <div className="ambr-card" style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <h3 style={{ margin: '0 0 0.3rem', fontSize: '0.875rem', fontWeight: 700, color: '#0f172a' }}>Top Bundle Companions</h3>
            <p style={{ margin: '0 0 0.75rem', fontSize: '0.75rem', color: '#94a3b8' }}>
              Orders containing each product alongside {data.name} · one order may include multiple companions
            </p>
            {data.companions.length === 0 ? (
              <div style={{ fontSize: '0.82rem', color: '#94a3b8', textAlign: 'center', padding: '2rem 0' }}>
                No companion products found — all orders are standalone.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                {data.companions.slice(0, 6).map((c, i) => (
                  <div key={i} style={{ cursor: 'pointer' }} onClick={() => openDrill({ type: 'companion', productName: c.name })}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '0.5rem', marginBottom: '0.25rem', fontSize: '0.82rem' }}>
                      <span style={{ color: '#2563eb', fontWeight: 500, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: 'underline', textDecorationStyle: 'dotted' }}>{c.name}</span>
                      <span style={{ color: '#2563eb', fontWeight: 700, flexShrink: 0, fontSize: '0.78rem' }}>
                        {c.orderCount} ({Math.round(c.orderCount / Math.max(data.totalOrders, 1) * 100)}% of total) →
                      </span>
                    </div>
                    <div style={{ height: 5, background: '#f1f5f9', borderRadius: 999, overflow: 'hidden' }}>
                      <div style={{ height: '100%', borderRadius: 999, width: `${(c.orderCount / maxCompanion) * 100}%`, background: i < 2 ? 'linear-gradient(90deg,#2563eb,#3b82f6)' : i < 4 ? 'linear-gradient(90deg,#0ea5e9,#38bdf8)' : '#cbd5e1', transition: 'width 0.6s ease' }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Daily Acquisition Trend */}
        <div className="ambr-card" style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', marginBottom: '1.25rem' }}>
          <h3 style={{ margin: '0 0 0.3rem', fontSize: '0.875rem', fontWeight: 700, color: '#0f172a' }}>Daily Acquisition Trend</h3>
          <p style={{ margin: '0 0 0.75rem', fontSize: '0.75rem', color: '#94a3b8' }}>Click a bar to drill into that day's orders</p>
          <div className="ambr-chart-sm" style={{ height: 200, overflow: 'visible' }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={data.byDay} barSize={28} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}
                onClick={(d) => { if (d?.activeLabel) { openDrill({ type: 'day', date: String(d.activeLabel) }); } }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip content={<ChartTip />} cursor={{ fill: '#f8fafc' }} />
                <Legend wrapperStyle={{ fontSize: '0.78rem', paddingTop: '0.5rem' }} />
                <Bar dataKey="bundled" name="Bundled" stackId="a" fill="#2563eb" radius={[0, 0, 0, 0]} style={{ cursor: 'pointer' }} />
                <Bar dataKey="standalone" name="Standalone" stackId="a" fill="#16a34a" radius={[4, 4, 0, 0]} style={{ cursor: 'pointer' }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Purchase Mix | Payment Type | Order Status */}
        <div className="ambr-three-grid">
          <div className="ambr-card" style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', overflow: 'visible' }}>
            <h3 style={{ margin: '0 0 0.3rem', fontSize: '0.875rem', fontWeight: 700, color: '#0f172a' }}>Purchase Mix</h3>
            <p style={{ margin: '0 0 0.75rem', fontSize: '0.75rem', color: '#94a3b8' }}>Standalone vs bundled split</p>
            <div className="ambr-chart-sm" style={{ height: 200, overflow: 'visible', paddingBottom: '0.5rem' }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={donutData} cx="50%" cy="50%" innerRadius={55} outerRadius={80} paddingAngle={3} dataKey="value" strokeWidth={0}
                    onClick={(d) => { if (d?.name) { openDrill({ type: (d.name as string).toLowerCase() as 'standalone' | 'bundled' }); } }}>
                    {donutData.map((d, i) => <Cell key={i} fill={d.color} style={{ cursor: 'pointer' }} />)}
                  </Pie>
                  <text x="50%" y="50%" textAnchor="middle" dominantBaseline="central">
                    <tspan x="50%" dy="-0.4em" fontSize="1.75rem" fontWeight="800" fill="#0f172a">{data.totalOrders}</tspan>
                    <tspan x="50%" dy="1.5em" fontSize="0.7rem" fill="#94a3b8" fontWeight="600" letterSpacing="0.06em">TOTAL</tspan>
                  </text>
                  <Tooltip content={<ChartTip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
              {donutData.map(d => (
                <button key={d.name} onClick={() => openDrill({ type: d.name.toLowerCase() as 'standalone' | 'bundled' })}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.78rem', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: d.color }} />
                  <span style={{ color: '#475569', fontWeight: 500 }}>{d.name}</span>
                  <span style={{ color: d.color, fontWeight: 700 }}>{((d.value / Math.max(data.totalOrders, 1)) * 100).toFixed(1)}%</span>
                </button>
              ))}
            </div>
          </div>

          {data.byPaymentMethod.length > 0 && (
            <div className="ambr-card" style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
              <h3 style={{ margin: '0 0 0.3rem', fontSize: '0.875rem', fontWeight: 700, color: '#0f172a' }}>By Payment Type</h3>
              <p style={{ margin: '0 0 0.875rem', fontSize: '0.75rem', color: '#94a3b8' }}>How {data.name} orders were paid</p>
              <PaymentStatusDonut
                data={data.byPaymentMethod.map((d, i) => ({ label: d.method, value: d.orderCount, color: PAYMENT_COLORS[i % PAYMENT_COLORS.length] }))}
                total={data.totalOrders}
                onSliceClick={(label) => openDrill({ type: 'payment', method: label })}
              />
            </div>
          )}

          {data.byStatus.length > 0 && (
            <div className="ambr-card" style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
              <h3 style={{ margin: '0 0 0.3rem', fontSize: '0.875rem', fontWeight: 700, color: '#0f172a' }}>By Order Status</h3>
              <p style={{ margin: '0 0 0.875rem', fontSize: '0.75rem', color: '#94a3b8' }}>Fulfillment status of {data.name} orders</p>
              <PaymentStatusDonut
                data={data.byStatus.map(d => ({ label: d.status, value: d.orderCount, color: statusColor(d.status) }))}
                total={data.totalOrders}
                onSliceClick={(label) => openDrill({ type: 'status', status: label })}
              />
            </div>
          )}
        </div>

      </>}

      {/* ── Society tab (AMBR only) ─────────────────────────── */}
      {activeTab === 'society' && isAmbrSelected && societyData && <>

        <div style={{ marginBottom: '1.25rem' }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.6rem' }}>
            Membership Overlap
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
            {[
              { label: 'Institute Only', testId: 'overlap-institute-only', value: data.totalOrders - societyData.withSociety, sub: 'AMBR · no society',  color: '#2563eb', bg: '#eff6ff', drill: { type: 'instituteOnly' } as DrillFilter },
              { label: 'Both',           testId: 'overlap-both',           value: societyData.withSociety,                     sub: 'AMBR + Society',    color: '#7c3aed', bg: '#f5f3ff', drill: { type: 'withSociety' }  as DrillFilter },
              { label: 'Society Only',   testId: 'overlap-society-only',   value: societyData.societyOnly,                     sub: 'Society · no AMBR', color: '#0891b2', bg: '#ecfeff', drill: societyData.societyOnly > 0 ? { type: 'societyOnlyDrill' } as DrillFilter : null },
            ].map((k, i) => (
              <div key={i} onClick={() => k.drill && openDrill(k.drill)}
                style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '1rem 1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', position: 'relative', overflow: 'hidden', cursor: k.drill ? 'pointer' : 'default', transition: 'box-shadow 0.15s, transform 0.15s' }}
                onMouseEnter={e => { if (k.drill) { (e.currentTarget as HTMLDivElement).style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'; (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-1px)'; } }}
                onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)'; (e.currentTarget as HTMLDivElement).style.transform = 'none'; }}
              >
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: k.color }} />
                <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.4rem' }}>{k.label}</div>
                <div data-testid={k.testId} style={{ fontSize: '2rem', fontWeight: 800, color: k.color, lineHeight: 1, marginBottom: '0.3rem', letterSpacing: '-0.03em' }}>{k.value}</div>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', background: k.bg, color: k.color, fontSize: '0.7rem', fontWeight: 600, padding: '0.15rem 0.5rem', borderRadius: 999 }}>
                  {k.sub}{k.drill && <span style={{ opacity: 0.6 }}>→</span>}
                </div>
              </div>
            ))}
          </div>
        </div>

        {societyData.bySociety.length > 0 && (
          <div className="ambr-card" style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', marginBottom: '1.25rem' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1rem', gap: '1rem', flexWrap: 'wrap' }}>
              <div>
                <h3 style={{ margin: '0 0 0.2rem', fontSize: '0.875rem', fontWeight: 700, color: '#0f172a' }}>Society Membership Breakdown</h3>
                <p style={{ margin: 0, fontSize: '0.75rem', color: '#94a3b8' }}>
                  {societyView === 'combined'
                    ? 'Orders where members bought both AMBR and a society membership · click to drill in'
                    : 'Orders containing only a society membership (no AMBR) · tracked from deploy'}
                </p>
              </div>
              <div style={{ display: 'flex', gap: 2, background: '#f1f5f9', borderRadius: 8, padding: 2, flexShrink: 0 }}>
                {(['combined', 'standalone'] as const).map(v => (
                  <button key={v} onClick={() => {
                    setSocietyView(v);
                    trackUserEvent('product_analytics.society_view_changed', { view: v });
                  }} style={{ padding: '0.25rem 0.75rem', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600, background: societyView === v ? '#fff' : 'transparent', color: societyView === v ? '#7c3aed' : '#94a3b8', boxShadow: societyView === v ? '0 1px 3px rgba(0,0,0,0.1)' : 'none', transition: 'all 0.15s' }}>
                    {v === 'combined' ? 'Combined' : 'Society Only'}
                  </button>
                ))}
              </div>
            </div>
            {(() => {
              const rows = [...societyData.bySociety].sort((a, b) =>
                societyView === 'combined' ? b.withAmbr - a.withAmbr : b.standalone - a.standalone
              );
              const maxVal = Math.max(...rows.map(r => societyView === 'combined' ? r.withAmbr : r.standalone), 1);
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {rows.map((s, i) => {
                    const val = societyView === 'combined' ? s.withAmbr : s.standalone;
                    const code = s.sku.replace(/^ASD-/i, '');
                    const clickable = societyView === 'combined' || (societyView === 'standalone' && val > 0);
                    return (
                      <div key={s.sku}
                        onClick={() => { if (!clickable) { return; } if (societyView === 'combined') { openDrill({ type: 'withSociety', societySku: s.sku }); } else { openDrill({ type: 'societyOnlyDrill', societySku: s.sku }); } }}
                        style={{ cursor: clickable ? 'pointer' : 'default', borderRadius: 6, padding: '0.15rem 0.3rem', margin: '0 -0.3rem', transition: 'background 0.15s' }}
                        onMouseEnter={e => { if (clickable) { (e.currentTarget as HTMLDivElement).style.background = '#f8fafc'; } }}
                        onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.2rem', fontSize: '0.82rem' }}>
                          <span style={{ color: '#334155', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: i < 3 ? '#7c3aed' : '#a78bfa', flexShrink: 0 }} />
                            {code}
                          </span>
                          <span data-testid={societyView === 'combined' ? `society-combined-${code.toLowerCase()}` : `society-standalone-${code.toLowerCase()}`}
                            style={{ fontWeight: 700, color: '#7c3aed', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                            {val}{clickable && <span style={{ color: '#cbd5e1', fontSize: '0.7rem' }}>→</span>}
                          </span>
                        </div>
                        <div style={{ height: 5, background: '#f1f5f9', borderRadius: 999, overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${(val / maxVal) * 100}%`, background: i < 3 ? 'linear-gradient(90deg,#7c3aed,#a78bfa)' : '#c4b5fd', borderRadius: 999, transition: 'width 0.6s ease' }} />
                        </div>
                      </div>
                    );
                  })}
                  {societyView === 'standalone' && rows.every(r => r.standalone === 0) && (
                    <div style={{ fontSize: '0.78rem', color: '#94a3b8', textAlign: 'center', padding: '0.5rem 0' }}>
                      Society-only orders accumulate going forward from deployment.
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}

      </>}

      {/* Drill-down drawer */}
      {drill && (
        <DrillDrawer
          orders={productOrders?.orders ?? []}
          societyOnlyOrders={isAmbrSelected ? filteredSocietyOnlyOrders : []}
          filter={drill}
          productSku={selectedSku}
          onClose={() => setDrill(null)}
          isLoading={drill.type !== 'societyOnlyDrill' && ordersLoading}
        />
      )}
    </div>
  );
}

export default ProductAnalyticsDashboard;
